import React from 'react';

import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {LoadingState} from 'models/reduceLoadingState';
import ListingSettingsDropdown from 'nav/ListingSettingsDropdown';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import {getSortItemSectionsForSearchParams} from 'nav/sidepanel/header/SidepanelListingSettingsDropdown';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {AVAILABLE_PAGE_SIZES} from 'nav/sidepanel/PageSizeUtil';
import ClearSearchButton from 'search/ClearSearchButton';
import QueryInput from 'search/QueryInput';
import {
  FilterKey,
  LISTING_ERROR_MESSAGE,
  SearchParams,
  SearchType,
  Sort,
  SortKey,
} from 'search/SearchConstants';
import * as SearchPreferences from 'search/SearchPreferences';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ResultsPager from 'ui-library/ResultsPager';

import WorkflowTable from './components/WorkflowTable';
import {WorkflowSelector} from './reduceWorkflows';
import {getWorkflowsBaseUrl} from './util/WorkflowUtil';
import {WorkflowDashboardDetails, WorkflowSummary, WorkflowTemplate} from './WorkflowConstants';
import WorkflowsFiltersContainer from './WorkflowsFiltersContainer';
import {getDefaultWorkflowsSearchParams} from './WorkflowsSearchParams';

interface EnhancedProps {
  workflows: Array<WorkflowSummary> | null;
  workflowIdToDashboardDetails: {[workflowId: string]: WorkflowDashboardDetails} | null;
  workflowTemplates: Array<WorkflowTemplate> | null;
  isLoading: Boolean;
}

class WorkflowDashboardScreenConnector extends DataConnector<SearchProps, EnhancedProps> {
  renderLoading(): JSX.Element {
    return (
      <WorkflowDashboardScreen
        {...this.props}
        workflows={null}
        workflowIdToDashboardDetails={null}
        workflowTemplates={null}
        isLoading={true}
      />
    );
  }
  enhanceProps(): EnhancedProps {
    const props = {
      workflows: null,
      workflowIdToDashboardDetails: null,
      workflowTemplates: null,
      isLoading: true,
    };
    if (!this.props.searchResults) {
      return props;
    }
    const state = this.reduxState();
    const workflowSelector = new WorkflowSelector(state.models.workflows);
    const workflows = this.props.searchResults.ids.map((id) => workflowSelector.getWorkflowSummary(id));
    const workflowIdToDashboardDetails = this.getOrFetchMultipleRequired(
      ManagedModelType.WORKFLOW_DASHBOARD_DETAILS,
      this.props.searchResults.ids
    );
    const workflowTemplates = workflowSelector.getAllWorkflowTemplates();
    return {
      ...props,
      workflows: workflows.every((workflow: WorkflowSummary) => Boolean(workflow))
        ? (workflows as Array<WorkflowSummary>)
        : null,
      workflowIdToDashboardDetails,
      workflowTemplates,
      isLoading: false,
    };
  }
}

export class WorkflowDashboardScreen extends React.Component<SearchProps & EnhancedProps> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSearch = (searchParams: SearchParams) => {
    this.props.onSearch(searchParams);
    SearchPreferences.rememberAppliedSortsToUserPreference(
      NavConstants.Section.WORKFLOWS,
      searchParams.sorts
    );
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSetSort = (sort: Sort) => {
    this.props.onUpdateSortOrder([sort]);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleClear = () => {
    this._handleSearch(SearchUtil.clearSearchParams(this.props.searchParams, [FilterKey.TYPES]));
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _getEmptyMessage = () =>
    this.props.loadingState === LoadingState.FAILED ? LISTING_ERROR_MESSAGE : 'No workflows';

  render(): JSX.Element {
    const pagination = {
      offset: this.props.searchParams.offset,
      limit: this.props.searchParams.limit,
      total: this.props.searchResults ? this.props.searchResults.total : 0,
      isExact: this.props.searchResults ? this.props.searchResults.isExact : true,
    };
    return (
      <div className="u-flex-full-height">
        <div className="u-flex-full-height">
          <ListingHeader
            isExpanded={true}
            queryInput={
              <QueryInput
                onUpdateQuery={this.props.onUpdateQueryString}
                query={this.props.searchParams.query}
              />
            }
            filtersContainer={
              <WorkflowsFiltersContainer
                filters={this.props.searchParams.filters}
                filterErrors={this.props.filterErrors}
                onChangeFilters={this.props.onUpdateFilters}
                workflowTemplates={this.props.workflowTemplates}
                clearButton={
                  SearchUtil.hasQueryOrNonDefaultFilters(
                    this.props.searchParams,
                    [],
                    [ModelTypeConstants.WORKFLOW]
                  ) ? (
                    <ClearSearchButton onClear={this._handleClear} />
                  ) : null
                }
              />
            }
          />
          <div className="u-flex-display">
            <ResultsPager
              isLoading={!this.props.searchResults}
              setOffset={this.props.onUpdateOffset}
              pagination={pagination}
            />
            <ListingSettingsDropdown
              sortConfig={{
                availableSortOptions: getSortItemSectionsForSearchParams(this.props.searchParams, [
                  SortKey.SORT_NAME_KEY,
                  SortKey.SORT_CREATED_AT_KEY,
                  SortKey.SORT_IDENTIFIER_KEY,
                ]),
                sorts: this.props.searchParams.sorts,
                onSetSort: (sortKey: string, reverse: boolean) => {
                  this._handleSetSort({key: sortKey, reverse});
                },
              }}
              pageSizeConfig={{
                pageSize: this.props.searchParams.limit,
                availablePageSizes: AVAILABLE_PAGE_SIZES,
                onSetPageSize: this.props.onUpdatePageSize,
              }}
            />
          </div>
          {!this.props.isLoading &&
          this.props.workflows &&
          this.props.workflowTemplates &&
          this.props.workflowIdToDashboardDetails &&
          this.props.workflows.every(
            // @ts-expect-error -- line above checks for existence of workflowIdToDashboardDetails
            (workflow) => this.props.workflowIdToDashboardDetails[workflow.id] != null
          ) ? (
            <WorkflowTable
              workflows={this.props.workflows}
              workflowIdToDashboardDetails={this.props.workflowIdToDashboardDetails}
              emptyMessage={this._getEmptyMessage()}
              searchResults={this.props.searchResults}
              sorts={this.props.searchParams.sorts}
              onSetColumnHeaderSort={this._handleSetSort}
            />
          ) : (
            <CenteredHorizontalThrobber />
          )}
        </div>
      </div>
    );
  }
}

function getWorkflowsURL(prevProps: SearchProps, props: SearchProps): NavigationUpdate {
  return {
    url: getNavigationURL(getWorkflowsBaseUrl(), props.searchParams, props.filterErrors),
  };
}

export default withSearching(
  WorkflowDashboardScreenConnector.connect(WorkflowDashboardScreen),
  SearchType.WORKFLOW,
  NavConstants.Section.WORKFLOWS,
  {defaultSearchParams: getDefaultWorkflowsSearchParams(), getNavigationUpdate: getWorkflowsURL}
);
